import { Menu, Transition } from "@headlessui/react";
import { bottom } from "@popperjs/core";
import { useContext, useEffect, useRef, useState } from "react";
import { usePopper } from "react-popper";
import { Link, useParams } from "react-router-dom";
import S3Image from "src/components/shared/S3Image";
import { AuthContext } from "src/context/auth";
import { classNames } from "src/helpers/classNames";
import Image from "../elements/Image";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import useDevice from "src/hooks/useDevice";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import CollapsibleGroup from "./CollapsibleGroup";
import { useSiteSettings } from "src/context/SiteSettingsContext";

const UserDropdown = ({ data, xPlacement, showName = true, mordernDesign, nameClasses, ...props }) => {
  const { page, form, document } = useUrlQueryParams();
  const { content_value } = useParams();

  const { site } = useSiteSettings();
  const device = useDevice();
  const { user: userDetails } = useContext(AuthContext);
  const popperElRef = useRef(null);
  const [targetElement, setTargetElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  const user = userDetails?._id ? userDetails : props?.user;

  const menu_id = page || document || form || content_value;

  const placements = {
    left: "bottom-start",
    right: "bottom-end",
    "": bottom,
  };

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: placements[xPlacement],
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, mordernDesign ? 8 : 0],
        },
      },
    ],
  });

  const isMobilePortrait = device === "mobilePortrait";

    useEffect(() => {
      if (isMobilePortrait && site?.header_menu_height > 0) {
        setHeaderHeight(site.header_menu_height);
      } else {
        setHeaderHeight(0);
      }
    }, [device, site?.header_menu_height]);

  return (
    <>
      <Menu as="div">
        {({ open }) => (
          <>
            <Menu.Button
              ref={setTargetElement}
              className={classNames("flex select-none items-center", mordernDesign && "w-full")}
              {...(!showName && {
                "data-tooltip-id": "user-tooltip",
                "data-tooltip-content": `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "User",
              })}>
              <div className={classNames("relative flex w-full items-center gap-x-3 transition-all duration-200", mordernDesign ? "h-12 w-full flex-row-reverse justify-end xl:h-16" : "justify-between rounded-md border-transparent")} style={{ height: headerHeight > 0 ? headerHeight * 0.8 : '1.75rem' }}>
                {mordernDesign && device === "desktop" && (
                  <div className={classNames("ml-auto flex-shrink-0 transition-all duration-200", open ? "-rotate-180" : "")}>
                    <ChevronDownIcon className="h-5 w-5 stroke-1 text-leftNavTextColor" />
                  </div>
                )}
                {showName && (
                  <div className={classNames("hidden max-w-[200px] truncate font-medium xl:block", mordernDesign ? "ml-3 text-sm font-semibold text-leftNavTextColor md:text-base xl:text-lg" : "text-sm text-headerTextColor", nameClasses)}>
                    {`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
                  </div>
                )}

                <div className={classNames("aspect-square flex-shrink-0 overflow-hidden", mordernDesign ? "w-7 rounded-lg xl:w-10" : "w-7 rounded-full")}
                  style={{ width: headerHeight > 0 ? headerHeight * 0.8 : '1.75rem'}}
                >
                  <S3Image
                    src={user?.image}
                    alt={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
                    width={"100%"}
                    height={"100%"}
                    effect={"blur"}
                    classes={"object-cover"}
                    userNoImage={user?._id && !user.image ? true : false}
                  />
                </div>
              </div>
            </Menu.Button>

            <Transition
              show={open}
              leave="transition duration-75"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="z-50"
              beforeEnter={() => setPopperElement(popperElRef.current)}
              afterLeave={() => setPopperElement(null)}
              ref={popperElRef}
              style={styles.popper}
              {...attributes.popper}>
              <Menu.Items className={classNames("w-64 origin-top-right select-none divide-y divide-slate-100 rounded-md border border-gray-200 bg-white p-1 shadow-lg ring-0 ", mordernDesign ? "-mb-2 sm:w-72" : "sm:w-48")}>
                <div className="max-h-80 overflow-y-auto py-1">
                  {data?.dropdownData
                    ?.filter((item) => !item.hidden)
                    .map((item) => (
                      <Menu.Item key={item._id}>
                        {item.type === "divider" ? (
                          <div className="my-2 border-t border-gray-200" />
                        ) : item.type === "label" ? (
                          <div className="px-3 py-2 text-xs font-semibold uppercase text-gray-400">{item.label}</div>
                        ) : item.type === "user" ? (
                          <div className={classNames("flex w-full items-center gap-3 rounded px-3 py-2.5 text-gray-700 transition-all duration-200")}>
                            <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full text-base">
                              {item?.icon ? (
                                <item.icon className="stroke-1.5 h-4 w-4 " />
                              ) : item?.image ? (
                                <S3Image
                                  src={user?.image}
                                  alt={`${user?.first_name || ""} ${user?.last_name || ""}`.trim()}
                                  width={"100%"}
                                  height={"100%"}
                                  effect={"blur"}
                                  classes={"object-cover"}
                                  userNoImage={user?._id && !user.image ? true : false}
                                />
                              ) : (
                                <></>
                              )}
                            </div>
                            <div className="text-sm">{item.label}</div>
                          </div>
                        ) : item.type === "group" ? (
                          <CollapsibleGroup
                            item={item}
                            menu_id={menu_id}
                          />
                        ) : item.link ? (
                          <Link
                            to={item.link}
                            className={classNames(
                              "my-1 flex w-full items-center gap-3 rounded px-3 py-2.5 transition-all duration-200",
                              item._id === menu_id || item.slug === menu_id ? "bg-gray-100 text-leftNavActiveTextColor" : "",
                              item.standout ? "text-rose-500 hover:bg-rose-100 hover:text-rose-500" : "hover:bg-gray-100 hover:text-leftNavActiveTextColor",
                              item?.disabled ? "cursor-not-allowed opacity-50" : "",
                            )}>
                            {(item.icon || item?.image) && (
                              <div className="flex h-5 w-5 items-center justify-center text-base">
                                {item.icon ? (
                                  <item.icon className="stroke-1.5 h-4 w-4" />
                                ) : (
                                  <Image
                                    src={item?.image}
                                    alt={item?.name || ""}
                                    width={"100%"}
                                    height={"100%"}
                                    effect={"blur"}
                                    classes={"object-cover"}
                                  />
                                )}
                              </div>
                            )}
                            <div className="max-w-[200px] overflow-hidden truncate whitespace-nowrap text-sm">{item.label}</div>
                          </Link>
                        ) : (
                          <button
                            disabled={item?.disabled}
                            type="button"
                            className={classNames(
                              "flex w-full items-center gap-3 rounded px-3 py-2.5 text-gray-700 transition-all duration-200",
                              item.standout ? "text-rose-500 hover:bg-rose-100 hover:text-rose-500" : "hover:bg-leftNavActiveColor hover:text-leftNavActiveTextColor",
                            )}
                            onClick={item.onclick || (() => {})}>
                            {(item.icon || item?.image) && (
                              <div className="flex h-5 w-5 items-center justify-center text-base">
                                {item.icon ? (
                                  <item.icon className="stroke-1.5 h-4 w-4" />
                                ) : (
                                  <Image
                                    src={item?.image}
                                    alt={item?.name || ""}
                                    width={"100%"}
                                    height={"100%"}
                                    effect={"blur"}
                                    classes={"object-cover"}
                                  />
                                )}
                              </div>
                            )}
                            <div className="max-w-[200px] overflow-hidden truncate whitespace-nowrap text-sm">{item.label}</div>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                </div>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </>
  );
};

export default UserDropdown;
